import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Row, Col, ButtonGroup, Button } from 'reactstrap';

import MaybeExternalLink from '../../common/MaybeExternalLink';
import { createFormField } from '../../../models/FormField';
import { quantityValid, productCountValid } from '../../../util/formValidators';
import { createForm } from '../../../models/Form';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import FieldError from '../../form/FieldError';
import ProductAvailabilityText from '../../product/ProductAvailabilityText';
import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import UIStore from '../../../store/UIStore';
import {
  calculateNearestProductQuantityWithQuantityStep,
  calculateProductPackageSizeWithQuantity
} from '../../../util/number';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import Price from '../../product/Price';
import AccountStore from '../../../store/AccountStore';
import ProductStore from '../../../store/ProductStore';
import CartStore from '../../../store/CartStore';
import Analytics from '../../../analytics/Analytics';
import ProductListProduct from '../../../models/ProductListProduct';
import ProductClass from '../../../types/ProductClass';
import ProductPrice from '../../product/ProductPrice';
import ProductPriceInfo from '../../../models/ProductPriceInfo';
import ProductPagePrice from '../../product/ProductPagePrice';
import ConfigStore from '../../../store/ConfigStore';
import ProductRecurringOrderType from '../../../types/ProductRecurringOrderType';

const CUSTOM_ASPECT_RATIO = '120';

@observer
export class WishListProductRow extends Component {
  form = null;
  maxQuantity = 99999;
  defaultValue = 0;
  accessToken = null;
  quantityField = null;

  constructor(props) {
    super(props);

    this.defaultValue = calculateProductPackageSizeWithQuantity(
      this.getQuantityStep(this.state.product),
      this.state.product.quantity
    );
    const formField = this.createQuantityFormFields(this.props.product);
    this.initializeQuantityFormField(formField);
  }

  state = {
    totalPrice: 0,
    product: this.props.product
  };

  componentDidMount() {
    this.calculateTotalPrice();
  }

  getAccessToken = () => {
    const { match } = this.props;
    if (
      match.params.hasOwnProperty('accessToken') &&
      match.params.accessToken
    ) {
      this.accessToken = match.params.accessToken.length;
    }
  };

  createQuantityFormFields = (product) => {
    let formFields = {};
    formFields['quantity' + product.product_id] = createFormField(
      {
        value: String(product.quantity)
      },
      [
        quantityValid(this.maxQuantity),
        productCountValid(this.getQuantityStep(product))
      ]
    );
    return formFields;
  };

  initializeQuantityFormField = (formFields) => {
    this.form = null;
    this.form = createForm(formFields);
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4
      },
      sizes
    );
    return (
      <Col className="WishListProductRow__product-list-header" {...actualSizes}>
        <label className="WishListProductRow__product-list-header-label">
          {label}
        </label>
      </Col>
    );
  };

  renderProductImageOnLargeDisplays = (product) => {
    const element = (
      <ProductImage
        product={product}
        productImage={this.createProductImage(product)}
        size="small"
        forceAspectRatio={true}
        overrideConfigStoreAspectRatio={true}
        customAspectRatio={CUSTOM_ASPECT_RATIO}
        lazyLoading={false}
      />
    );
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderProductName = (product) => {
    const element =
      this.renderReadableIdByProductClass(product) + ' - ' + product.name;
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderElementWithLinkIfExists = (element) => (
    <MaybeExternalLink to={this.state.product.link}>
      {element}
    </MaybeExternalLink>
  );

  renderProductAvailabilityText = (product) => (
    <div>
      <ProductAvailabilityText availabilityHtml={product.availability_html} />
    </div>
  );

  renderProductVariations = (product) => {
    return (
      <ul className="WishListProductRow_product-variations">
        {product.variations.map((variation, i) => {
          if (!variation) {
            return null;
          }

          return <li key={i}>{variation}</li>;
        })}
      </ul>
    );
  };

  createProductImage = (product) => {
    const url = product.image;
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: this.getProductIdByProductClass(product),
          sizes: {
            small: url
          }
        })
      : null;
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI ||
      product.class === ProductClass.COLLECTION
      ? product.id
      : product.product_id;
  };

  renderReadableIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI
      ? product.id
      : product.product_id;
  };

  renderAddToCartQuantity = () => {
    if (!this.state.product.price_info) {
      return null;
    }
    this.quantityField = this.form.fields.get(
      'quantity' + this.state.product.product_id
    );
    return (
      <QuantityFieldGroup
        key={this.state.product.product_id}
        disabled={!!this.ifQuantityFieldDisabled()}
        className={
          'WishListProductRow__product-list-quantity-group ' +
          this.state.product.product_id
        }
        field={this.quantityField}
        quantityStep={this.getQuantityStep(this.state.product)}
        defaultValue={this.defaultValue}
        maxQuantity={this.maxQuantity}
        unit={this.state.product.stock_unit}
        onUpdateProductQuantity={this.updateProductQuantity.bind(this)}
      />
    );
  };

  ifQuantityFieldDisabled = () => {
    const { match } = this.props;
    return (
      match.params.hasOwnProperty('accessToken') && match.params.accessToken
    );
  };

  getQuantityStep = (product) => {
    return product && product.package_size > 1 ? product.package_size : 1;
  };

  showDiscountPriceIfExisting = () => {
    const { price_info } = this.state.product;
    if (!price_info) {
      return null;
    }
    const product = ProductListProduct.create(this.state.product);
    const productPriceInfo = ProductPriceInfo.create(price_info);
    const price = (
      <ProductPrice
        className={'WishlistProductRow__product-row-product-page-price'}
        priceInfo={productPriceInfo}
      />
    );
    return (
      <ProductPagePrice
        className={'WishlistProductRow__product-row-product-page-price'}
        price={price}
        product={product}
      />
    );
  };

  renderTotalPriceAndQuantity = (product) => {
    return (
      <div className="WishListProductRow__product-list-price-and-quantity">
        {this.renderAddToCartQuantity(product)}
        {this.showTotalPriceIfExisting(product)}
        <FieldError
          field={this.form.fields.get(
            'quantity' + this.state.product.product_id
          )}
        />
      </div>
    );
  };

  showTotalPriceIfExisting = () => {
    const { price_info } = this.state.product;
    if (
      !price_info ||
      !this.quantityField.value ||
      this.quantityField.value === '0'
    ) {
      return;
    }
    return (
      <Price
        className="WishListProductRow__product-row-total-price"
        price={this.state.totalPrice}
      />
    );
  };

  renderWishListProductRowButtons = (product) => {
    const { accountStore, configStore } = this.props;
    const buyingAllowed = !(
      configStore.recurringOrder.enabled &&
      accountStore.loggedIn &&
      product.recurring_order_type ===
        ProductRecurringOrderType.RECURRING_ORDERS_ONLY
    );

    return (
      <div className="WishListProductRow__product-list-buttons">
        <ButtonGroup vertical>
          {buyingAllowed && (
            <Button
              color="success"
              disabled={this.validateAddToCartButton()}
              onClick={this.productAddToCart.bind(this, product)}
            >
              <FormattedMessage {...globalTranslations.addToCartButton} />
            </Button>
          )}
          <Button
            disabled={!!this.accessToken}
            color="primary"
            onClick={this.deleteProduct.bind(this, product)}
          >
            <FormattedMessage
              id="wishlist.DeleteProduct"
              defaultMessage="Delete"
            />
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  validateAddToCartButton = () => {
    return (
      this.form.valid === false ||
      !this.state.product.price_info ||
      this.quantityField.value === '0' ||
      !this.quantityField.value
    );
  };

  updateProductQuantity = (packageQuantity) => {
    const { accountStore, product } = this.props;
    if (accountStore.account.is_logged) {
      this.updateProductListIfLoggedIn(product, packageQuantity);
    } else {
      this.updateLocalProductList(product, packageQuantity);
    }
  };

  updateProductListIfLoggedIn = (product, packageQuantity) => {
    const { productStore } = this.props;
    const quantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );
    productStore
      .saveProduct({
        extendedId: product.id,
        quantity
      })
      .catch((err) => console.error(err));
    this.updateLocalPackageQuantity(quantity, packageQuantity);
    this.calculateTotalPrice();
  };

  updateLocalProductList = (product, packageQuantity) => {
    const newQuantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );
    this.updateLocalPackageQuantity(newQuantity, packageQuantity);
  };

  calculateTotalPrice = () => {
    const { price_info } = this.state.product;
    if (!price_info) {
      return;
    }

    this.setState({
      totalPrice:
        this.state.product.quantity *
        this.state.product.price_info.price.with_tax
    });

    if (this.state.product.package_size) {
      this.setState({
        totalPrice:
          this.state.product.quantity *
          this.state.product.package_size *
          this.state.product.price_info.price.with_tax
      });
    }
  };

  updateLocalPackageQuantity = (quantity, packageQuantity) => {
    const product = {
      ...this.state.product,
      quantity: Math.ceil(quantity)
    };
    this.setState({ product }, () => {
      this.props.onStorageUpdate(this.state.product);
      this.calculateTotalPrice();
    });
    this.defaultValue = packageQuantity;
  };

  productAddToCart = () => {
    const { cartStore, analytics } = this.props;
    let product = this.state.product;
    const activeProductId = this.state.product.id;
    const quantity = this.state.product.quantity;
    const price = product.price_info.price.with_tax;
    product = ProductListProduct.create(product);
    const analyticsList = [
      {
        product,
        quantity,
        activeProductId,
        price
      }
    ];

    this.form.validate();
    if (this.form.valid) {
      cartStore
        .addToCart(activeProductId, quantity, [], [], product)
        .then(() => {
          analytics.addToCart(analyticsList);
          analytics.cartStatus({
            cart: cartStore.cart
          });
        })
        .catch((err) => console.error(err));
    }
  };

  deleteProduct = (product) => {
    const { accountStore, productStore } = this.props;
    if (accountStore.account.is_logged) {
      const localProduct = { extendedId: product.id };
      productStore
        .deleteProduct(localProduct)
        .catch((err) => console.error(err));
      this.deleteLocalProduct();
    } else {
      this.deleteLocalProduct(product);
    }
  };

  deleteLocalProduct = (product) => {
    this.props.onProductDelete(product);
  };

  render() {
    const { uiStore, product } = this.props;
    this.getAccessToken();

    return (
      <Row className="WishListProductRow WishListProductRow__product-list-row">
        {uiStore.isDesktop && (
          <Col lg={2}>
            <div className="WishListProductRow__product-image">
              {this.renderProductImageOnLargeDisplays(product)}
            </div>
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          <div className="WishListProductRow__product-list-product">
            <div className="WishListProductRow__product-name">
              {this.renderProductName(product)}
              {this.renderProductAvailabilityText(product)}
              {this.renderProductVariations(product)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.showDiscountPriceIfExisting(product.price_info)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.renderTotalPriceAndQuantity(product)}
            </div>
          </div>
        </Col>
        {uiStore.isDesktop && (
          <Col md={2} className="WishListProductRow__product-list-col">
            {this.showDiscountPriceIfExisting(product.price_info)}
          </Col>
        )}
        {uiStore.isDesktop && (
          <Col md={4} className="WishListProductRow__product-list-col">
            {this.renderTotalPriceAndQuantity(product)}
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          {this.renderWishListProductRowButtons(product)}
        </Col>
      </Row>
    );
  }
}

WishListProductRow.propTypes = {
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  match: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  onStorageUpdate: PropTypes.func.isRequired,
  onProductDelete: PropTypes.func.isRequired
};

export default inject(
  'analytics',
  'accountStore',
  'configStore',
  'uiStore',
  'productStore',
  'cartStore'
)(WishListProductRow);



// WEBPACK FOOTER //
// ./src/components/wish-list/WishListProductRow/index.jsx